<script>
  // The group selector with its subgroup picker (CLAUDE.md "Web Subgroup
  // Picker"): a Discord server is ONE group whose entry expands into
  // checkable subgroups. Selection lives in stores/browse.svelte.js, so every
  // page that shows this picker shares it. Extracted from BrowseTabBar so
  // other pages can use the same one; the selection-dependent hint under the
  // subgroups was dropped (VISUALS.md "Show, don't tell").
  import {
    browseState,
    getSubgroupSelection,
    selectedServerGroup,
    toggleSubgroup,
  } from '../../lib/stores/browse.svelte.js';
  import { groupsState } from '../../lib/stores/groups.svelte.js';
  import { isUserAnonymous } from '../../lib/stores/auth.svelte.js';
  import { subgroupPickerLabel } from '../../lib/logic/groups-logic.js';

  let dropdownOpen = $state(false);
  let root = $state(/** @type {HTMLElement | null} */ (null));

  // The menu lists a signed-in viewer's groups; the trigger names whatever
  // group is selected either way (a restored selection, before sign-in lands).
  const menuGroups = $derived(isUserAnonymous() ? [] : groupsState.userGroups);
  const selected = $derived(selectedServerGroup());

  const label = $derived.by(() => {
    if (browseState.selectedGroupId === 'all') return 'All Binders';
    if (browseState.selectedGroupId === 'mine') return 'Mine Only';
    return selected ? subgroupPickerLabel(selected, getSubgroupSelection(selected)) : 'All Groups';
  });

  function hasSubgroups(group) {
    return (group.subgroups?.length || 0) > 0;
  }

  function selectAll() {
    browseState.selectedGroupId = 'all';
    dropdownOpen = false;
  }

  /** @param {import('../../lib/types.js').UserGroup} group */
  function selectGroup(group) {
    browseState.selectedGroupId = group.groupId;
    if (!hasSubgroups(group)) dropdownOpen = false; // servers with subgroups keep the menu open
  }

  /** @param {MouseEvent} e */
  function handleClickOutside(e) {
    if (root && !root.contains(/** @type {Node} */ (e.target))) dropdownOpen = false;
  }
</script>

<svelte:document onclick={handleClickOutside} />

<div class="group-selector">
  <span class="group-label">Group</span>
  <div class="group-dropdown" bind:this={root}>
    <button class="group-dropdown-trigger" onclick={() => (dropdownOpen = !dropdownOpen)}>
      {#if selected?.iconUrl}
        <img class="group-icon-small" src={selected.iconUrl} alt="" />
      {/if}
      <span>{label}</span>
      <svg
        class="dropdown-chevron"
        class:open={dropdownOpen}
        viewBox="0 0 24 24"
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </button>

    {#if dropdownOpen}
      <div class="group-dropdown-menu">
        <button
          class="group-option"
          class:selected={browseState.selectedGroupId === 'all'}
          onclick={selectAll}
        >
          All Binders
        </button>
        {#if menuGroups.length > 0}
          <div class="group-option-divider"></div>
        {/if}
        {#each menuGroups as group (group.groupId)}
          {@const selection = getSubgroupSelection(group)}
          <button
            class="group-option"
            class:selected={browseState.selectedGroupId === group.groupId}
            onclick={() => selectGroup(group)}
          >
            {#if group.iconUrl}
              <img class="group-icon" src={group.iconUrl} alt="" />
            {:else}
              <span class="group-icon-placeholder">
                {group.name.charAt(0).toUpperCase()}
              </span>
            {/if}
            <span class="option-name">{group.name}</span>
            {#if hasSubgroups(group)}
              <span class="option-count">{group.subgroups.length} groups</span>
            {/if}
          </button>
          {#if browseState.selectedGroupId === group.groupId && hasSubgroups(group)}
            <div class="subgroups">
              {#each group.subgroups as sub (sub.groupId)}
                <button
                  class="subgroup"
                  class:checked={selection.includes(sub.groupId)}
                  role="checkbox"
                  aria-checked={selection.includes(sub.groupId)}
                  onclick={() => toggleSubgroup(group, sub.groupId)}
                >
                  <span class="checkbox" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      width="11"
                      height="11"
                      fill="none"
                      stroke="#fff"
                      stroke-width="3"
                    >
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </span>
                  <span class="option-name">{sub.label}</span>
                  {#if sub.isMember}<span class="you-tag">YOU</span>{/if}
                </button>
              {/each}
            </div>
          {/if}
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .group-selector {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .group-label {
    font-size: 11px;
    color: var(--color-text-tertiary);
    font-family: var(--font-mono);
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .group-dropdown {
    position: relative;
  }

  .group-dropdown-trigger {
    all: unset;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--color-bg-surface);
    border: 1px solid var(--color-border-subtle);
    color: var(--color-text);
    padding: 6px 12px;
    border-radius: var(--radius-md);
    font-size: 13px;
    font-family: var(--font-sans);
    font-weight: 500;
    transition:
      border-color 0.15s,
      box-shadow 0.15s;
  }

  .group-dropdown-trigger:hover {
    border-color: var(--color-text-muted);
  }

  .dropdown-chevron {
    transition: transform 0.15s;
    color: var(--color-text-muted);
  }

  .dropdown-chevron.open {
    transform: rotate(180deg);
  }

  .group-icon-small {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    object-fit: cover;
  }

  .group-dropdown-menu {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    min-width: 240px;
    background: var(--color-bg-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    box-shadow: 0 4px 16px rgba(40, 30, 15, 0.1);
    padding: 4px;
    z-index: 60;
    animation: dropdown-in 0.12s ease-out;
  }

  @keyframes dropdown-in {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .group-option {
    all: unset;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    font-size: 13px;
    font-family: var(--font-sans);
    color: var(--color-text);
    border-radius: var(--radius-sm);
    transition: background 0.1s;
    box-sizing: border-box;
  }

  .group-option:hover {
    background: var(--color-accent-bg);
  }

  .group-option.selected {
    font-weight: 600;
    background: var(--color-accent-bg);
  }

  .group-option-divider {
    height: 1px;
    background: var(--color-border);
    margin: 4px 8px;
  }

  .group-icon {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  }

  .group-icon-placeholder {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--color-text-muted);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .option-name {
    flex: 1;
  }

  .option-count {
    font-size: 11px;
    color: var(--color-text-tertiary);
    font-family: var(--font-mono);
  }

  .subgroups {
    padding: 2px 0 6px;
  }

  .subgroup {
    all: unset;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 6px 12px 6px 42px;
    font-size: 13px;
    font-family: var(--font-sans);
    color: var(--color-text-secondary);
    border-radius: var(--radius-sm);
    box-sizing: border-box;
    transition: background 0.1s;
  }

  .subgroup:hover {
    background: var(--color-accent-bg);
  }

  .subgroup.checked {
    color: var(--color-text);
  }

  .checkbox {
    width: 15px;
    height: 15px;
    border: 1.5px solid var(--color-text-muted);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: var(--color-bg-surface);
  }

  .subgroup.checked .checkbox {
    background: var(--color-accent-dark);
    border-color: var(--color-accent-dark);
  }

  .checkbox svg {
    display: none;
  }

  .subgroup.checked .checkbox svg {
    display: block;
  }

  .you-tag {
    font-size: 10px;
    font-family: var(--font-mono);
    color: var(--color-text-tertiary);
    letter-spacing: 0.3px;
  }

  @media (max-width: 600px) {
    .group-label {
      display: none;
    }

    .group-dropdown-trigger {
      font-size: 12px;
      padding: 5px 10px;
    }
  }
</style>
